import React, { useState, useEffect, useCallback } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faRotateLeft,
  faCircleExclamation,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faXmark,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const TABS = ["Duplicate Accounts", "Refund Management"];

const maskPhone = (phone) => {
  if (!phone) return "—";
  const s = String(phone);
  return s.length > 4 ? "••••••" + s.slice(-4) : s;
};

const DuplicateAccounts = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await func.get(urls.DUPLICATE_PHONES_URL);
        if (res?.status === 200) setGroups(res.data.groups || []);
      } catch {
        toast.error("Failed to load duplicate accounts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-amber-800">
          These phone numbers are associated with more than one account. This may indicate duplicate registrations or shared devices.
        </p>
      </div>

      {groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 text-center py-16 text-gray-400 text-sm">
          No duplicate phone numbers detected
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                    <FontAwesomeIcon icon={faCircleExclamation} className="text-red-500 text-sm" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 font-mono">{maskPhone(g._id)}</p>
                    <p className="text-xs text-gray-400">{g.count} accounts sharing this number</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                  {g.count} duplicates
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {(g.users || []).map((u) => (
                  <div key={u.userId} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-mono text-gray-600 truncate">{u.userId}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        u.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                        u.status === 'BLOCKED' ? 'bg-red-100 text-red-700' :
                        u.isDeleted ? 'bg-gray-200 text-gray-500' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {u.isDeleted ? 'DELETED' : (u.status || 'UNKNOWN')}
                      </span>
                      <span className="text-xs text-gray-400">
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-IN") : ""}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RefundModal = ({ order, onClose, onSuccess }) => {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRefund = async () => {
    setLoading(true);
    try {
      const res = await func.post(urls.PROCESS_REFUND_URL + order._id, { note });
      if (res?.status === 200) {
        toast.success("Refund processed. User downgraded to free pack.");
        onSuccess();
        onClose();
      } else {
        toast.error(res?.data?.message || "Refund failed");
      }
    } catch {
      toast.error("Refund failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">Process Refund</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-xl p-4 space-y-1">
            <p className="text-xs text-gray-400">Transaction</p>
            <p className="font-semibold text-gray-800">{order.readableTransactionId || order._id}</p>
            <p className="text-sm text-gray-600">₹{order.amount} · {order.packType}</p>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-700">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
            This will downgrade the user to the Free pack immediately.
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Refund Note (optional)</label>
            <textarea
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Reason for refund..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 resize-none"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefund}
              disabled={loading}
              className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition disabled:opacity-60"
            >
              {loading ? "Processing…" : "Confirm Refund"}
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RefundManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const limit = 20;

  const fetch = useCallback(async (page, q) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (q) params.search = q;
      const res = await func.get(urls.REFUNDABLE_PAYMENTS_URL, { params });
      if (res?.status === 200) {
        setOrders(res.data.orders || []);
        setTotalPages(res.data.totalPages || 1);
        setTotal(res.data.total || 0);
      }
    } catch {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { fetch(1, ""); }, [fetch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(search);
    setCurrentPage(1);
    fetch(1, search);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
    fetch(p, activeSearch);
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search transaction ID…"
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30"
          />
        </div>
        <button type="submit" className="px-4 py-2 bg-[#C9A84C] text-white rounded-lg text-sm font-medium hover:bg-[#b8953f] transition">
          Search
        </button>
      </form>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50">
          <p className="text-sm font-semibold text-gray-700">{total} refundable transactions</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No transactions found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Transaction ID</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Pack</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orders.map((o) => (
                  <tr key={o._id} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3.5 font-mono text-xs text-gray-700">{o.readableTransactionId || o._id}</td>
                    <td className="px-5 py-3.5 font-semibold text-gray-800">₹{o.amount?.toLocaleString("en-IN")}</td>
                    <td className="px-5 py-3.5">
                      <span className="px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium capitalize">
                        {o.packType?.replace(/_/g, " ") || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">
                      {new Date(o.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => setSelected(o)}
                        className="flex items-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-700 transition px-3 py-1.5 border border-red-200 rounded-lg hover:bg-red-50"
                      >
                        <FontAwesomeIcon icon={faRotateLeft} className="text-[10px]" />
                        Refund
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-gray-50 flex items-center justify-between">
            <p className="text-xs text-gray-400">Page {currentPage} of {totalPages}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              </button>
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <RefundModal
          order={selected}
          onClose={() => setSelected(null)}
          onSuccess={() => fetch(currentPage, activeSearch)}
        />
      )}
    </div>
  );
};

const FraudDetection = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <FontAwesomeIcon icon={faShieldHalved} className="text-[#C9A84C]" />
          Fraud &amp; Refunds
        </h1>
        <p className="text-sm text-gray-400 mt-0.5">Duplicate account detection and payment refund management</p>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === i ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 0 ? <DuplicateAccounts /> : <RefundManagement />}
    </div>
  );
};

export default FraudDetection;
